"use client";

import React, { createContext, useContext } from "react";
import { api } from "@shared-server/api";
import {
  AppFeatureEnumValues,
  type AppFeatureEnumType,
} from "@shared-database/schema";
import type { AllDegradedFeatureType } from "@shared-server/api/routers/degraded-feature";

interface FeatureFlagContextType {
  loading: boolean;
  currentFeature: AppFeatureEnumType;
  featureIsDegraded: boolean;
  reasons: string[];
}

const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(
  undefined,
);

type FeatureFlagProviderProps = React.PropsWithChildren & {
  feature: AppFeatureEnumType;
};

const sortFeatures = (features: AllDegradedFeatureType[]) => {
  return features.sort((left, right) =>
    AppFeatureEnumValues.indexOf(left.feature) <
    AppFeatureEnumValues.indexOf(right.feature)
      ? -1
      : 0,
  );
};

/**
 * FeatureFlagProvider
 * Provides global feature flag state (degraded features, loading, error, refetch) via React Context.
 * Wrap your app with this provider to enable feature flagging.
 */
export const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = (
  props,
) => {
  const { data, isLoading } = api.degradedFeature.all.useQuery();

  const featureFlags = useFeatureFlags();

  let parentFeature: AppFeatureEnumType | undefined;
  if ("featureIsDegraded" in featureFlags && featureFlags.featureIsDegraded) {
    parentFeature = featureFlags.currentFeature;
  }

  const features = sortFeatures(
    data?.filter(
      (f) => f.feature === props.feature || f.feature === parentFeature,
    ) || [],
  );

  // Context value includes refetch for manual refresh
  const value = {
    loading: isLoading,
    currentFeature: props.feature,
    featureIsDegraded: isLoading
      ? false
      : features?.some((f) => f.degraded) || false,
    reasons: isLoading ? [] : features.map((f) => f.reason),
  };

  return (
    <FeatureFlagContext.Provider value={value}>
      {props.children}
    </FeatureFlagContext.Provider>
  );
};

/**
 * useFeatureFlags
 * Returns true if the given feature is degraded.
 * Must be used within FeatureFlagProvider.
 */
export const useFeatureFlags = ():
  | {
      currentFeature: AppFeatureEnumType;
      featureIsDegraded: boolean;
      reasons: string[];
    }
  | {
      contextUnAvailable: true;
    } => {
  const ctx = useContext(FeatureFlagContext);
  if (!ctx) {
    return {
      contextUnAvailable: true,
    };
  }
  return {
    currentFeature: ctx.currentFeature,
    featureIsDegraded: ctx.featureIsDegraded,
    reasons: ctx.reasons,
  };
};
